import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Dice1, Shuffle, Sparkles } from 'lucide-react';
import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов генератора
import GeneratorForm from './RandomNumberGeneratorPage/components/GeneratorForm';
import ResultsDisplay from './RandomNumberGeneratorPage/components/ResultsDisplay';
import GeneratorHistory from './RandomNumberGeneratorPage/components/GeneratorHistory';
import StatisticsDisplay from './RandomNumberGeneratorPage/components/StatisticsDisplay';
import SavedConfigsSidebar from './RandomNumberGeneratorPage/components/SavedConfigsSidebar';
import UseCasesSidebar from './RandomNumberGeneratorPage/components/UseCasesSidebar';
import InfoContent from './RandomNumberGeneratorPage/components/InfoContent';
import GeneratorPresets from './RandomNumberGeneratorPage/components/GeneratorPresets';

// Определение начального состояния формы
export const initialFormData = {
  min: '1',
  max: '100',
  count: '5',
  unique: true,
  sorting: 'none', // 'none', 'asc', 'desc'
  distribution: 'uniform', // 'uniform', 'normal', 'weighted'
  weightedValues: [
    { value: '1', weight: '1' },
    { value: '50', weight: '5' },
    { value: '100', weight: '1' }
  ],
  normalMean: '50',
  normalDeviation: '15',
  excludeValues: '',
  format: 'comma', // 'comma', 'newline', 'space'
  withoutRepetitions: false, // для предотвращения близких повторений
  seed: '',
  allowNegative: false,
  decimalPlaces: '0', // для дробных чисел
  customRange: false,
  customRangeValues: ''
};

// Пресеты для разных сценариев
export const generatorPresets = [
  { id: 'lottery', name: 'Лотерея 6 из 36', icon: 'ticket', config: { min: '1', max: '36', count: '6', unique: true, sorting: 'asc', distribution: 'uniform' } },
  { id: 'dice', name: 'Бросок кубика', icon: 'dice', config: { min: '1', max: '6', count: '1', unique: false, sorting: 'none', distribution: 'uniform' } },
  { id: 'password', name: 'Пароль из цифр', icon: 'key', config: { min: '0', max: '9', count: '8', unique: false, sorting: 'none', distribution: 'uniform' } },
  { id: 'loto', name: 'Русское лото', icon: 'ticket', config: { min: '1', max: '90', count: '15', unique: true, sorting: 'asc', distribution: 'uniform' } },
  { id: 'lottery-multi', name: 'Гослото 5 из 36 + 1 из 4', icon: 'ticket', config: { min: '1', max: '36', count: '5', unique: true, sorting: 'asc', distribution: 'uniform', customRange: true, customRangeValues: '1-4:1' } },
  { id: 'custom-range', name: 'Из разных диапазонов', icon: 'list', config: { min: '1', max: '100', count: '5', unique: true, sorting: 'none', distribution: 'uniform', customRange: true, customRangeValues: '1-10:2,50-100:3' } }
];

/**
 * Компонент страницы генератора случайных чисел
 */
const RandomNumberGeneratorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('random-number-generator'), []);
  const pageTitle = tool?.metaTitle || 'Генератор случайных чисел | Gensio';
  const metaDescription = tool?.metaDescription || 'Бесплатный онлайн генератор случайных чисел с различными настройками: диапазон, количество, сортировка, распределение вероятностей. Подходит для лотерей, розыгрышей, научных исследований и тестирования.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState([]);
  const [history, setHistory] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [errors, setErrors] = useState({});
  const [isGenerating, setIsGenerating] = useState(false);
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [showStatistics, setShowStatistics] = useState(false);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных конфигураций из localStorage ---
  useEffect(() => {
    const savedConfigsData = localStorage.getItem('savedGeneratorConfigs');
    if (savedConfigsData) {
      try {
        setSavedConfigs(JSON.parse(savedConfigsData));
      } catch (e) {
        console.error("Ошибка загрузки сохраненных конфигураций:", e);
      }
    }

    const historyData = localStorage.getItem('generatorHistory');
    if (historyData) {
      try {
        setHistory(JSON.parse(historyData));
      } catch (e) {
        console.error("Ошибка загрузки истории генераций:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      // Применяем пресет по subToolId, если такой есть
      const preset = generatorPresets.find(p => p.id === subToolId);
      if (preset) {
        newState = { ...initialFormData, ...preset.config };
        return newState;
      }

      // Иначе, применяем предустановленные параметры
      switch (subToolId) {
        case 'lottery':
          return { ...initialFormData, min: '1', max: '36', count: '6', unique: true, sorting: 'asc' };
        case 'dice':
          return { ...initialFormData, min: '1', max: '6', count: '1', unique: false };
        case 'custom-range':
          return { ...initialFormData, customRange: true, customRangeValues: '1-10:2,50-100:3' };
        case 'weighted':
          return { ...initialFormData, distribution: 'weighted' };
        case 'normal':
          return { ...initialFormData, distribution: 'normal' };
        case 'decimal':
          return { ...initialFormData, min: '0', max: '1', decimalPlaces: '3' };
        default:
          return { ...prev };
      }
    });

    setResults([]);
    setStatistics(null);
    setErrors({});
    setShowStatistics(false);

  }, [subToolId]);

  // Сохранение конфигурации генератора
  const saveConfig = (name) => {
    if (!name.trim()) return null;

    const newConfig = {
      id: Date.now().toString(),
      name: name,
      date: new Date().toISOString(),
      config: { ...formData }
    };

    const updatedConfigs = [...savedConfigs, newConfig];
    setSavedConfigs(updatedConfigs);
    localStorage.setItem('savedGeneratorConfigs', JSON.stringify(updatedConfigs));

    return newConfig;
  };

  // Удаление сохраненной конфигурации
  const deleteConfig = (id) => {
    const updatedConfigs = savedConfigs.filter(config => config.id !== id);
    setSavedConfigs(updatedConfigs);
    localStorage.setItem('savedGeneratorConfigs', JSON.stringify(updatedConfigs));
  };

  // Загрузка сохраненной конфигурации
  const loadConfig = (id) => {
    const configToLoad = savedConfigs.find(config => config.id === id);
    if (configToLoad) {
      setFormData(configToLoad.config);
      setResults([]);
      setStatistics(null);
      setShowStatistics(false);
    }
  };

  // Применение пресета
  const applyPreset = (presetId) => {
    const preset = generatorPresets.find(p => p.id === presetId);
    if (preset) {
      setFormData({ ...initialFormData, ...preset.config });
      setResults([]);
      setStatistics(null);
      setShowStatistics(false);
    }
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <Dice1 className="w-8 h-8 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
                {tool?.name || 'Генератор случайных чисел'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* Пресеты генератора */}
            <GeneratorPresets 
              presets={generatorPresets} 
              applyPreset={applyPreset} 
            />

            {/* --- Форма генератора --- */}
            <GeneratorForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isGenerating={isGenerating}
              setIsGenerating={setIsGenerating}
              setResults={setResults}
              setHistory={setHistory}
              history={history}
              setStatistics={setStatistics}
              showStatistics={showStatistics}
              setShowStatistics={setShowStatistics}
              saveConfig={saveConfig}
            />

            {/* --- Отображение результатов --- */}
            {results.length > 0 && !isGenerating && (
              <ResultsDisplay 
                results={results} 
                formData={formData}
                showStatistics={showStatistics}
                setShowStatistics={setShowStatistics}
              />
            )}

            {/* --- Отображение статистики --- */}
            {statistics && showStatistics && (
              <StatisticsDisplay 
                statistics={statistics} 
                formData={formData}
              />
            )}

            {/* --- История генераций --- */}
            {history.length > 0 && (
              <GeneratorHistory 
                history={history}
                setHistory={setHistory}
                loadFromHistory={(config, results) => {
                  setFormData(config);
                  setResults(results);
                }}
              />
            )}

            {/* --- Информационный контент о случайных числах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель сохраненных конфигураций --- */}
            <SavedConfigsSidebar 
              savedConfigs={savedConfigs}
              deleteConfig={deleteConfig}
              loadConfig={loadConfig}
            />

            {/* --- Панель сценариев использования --- */}
            <UseCasesSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-indigo-600 focus:ring-indigo-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Bubble Animation */
        @keyframes bubble {
          0% {
            transform: translateY(0);
            opacity: 0;
          }
          20% {
            opacity: 1;
          }
          80% {
            opacity: 1;
          }
          100% {
            transform: translateY(-15px);
            opacity: 0;
          }
        }
        .bubble-animation {
          animation: bubble 1.5s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default RandomNumberGeneratorPage;